import DrawerAccordion from '@components/DrawerAccordion';
import TagsFetchAutocomplete from '@components/TagsFetchAutocomplete';
import GroupsFetchAutocomplete from '@components/GroupsFetchAutocomplete';
import { usePrefetchPathwaySteps } from '@/hooks';
import { isTemplatePlaceholder } from '@/types';
import Age from '@pages/QuickFilterListCreatorPage/criteriaComponents/Age';
import CallingStatusForm from '@pages/QuickFilterListCreatorPage/criteriaComponents/CallingStatusForm';
import CommsPreferencesForm from '@pages/QuickFilterListCreatorPage/criteriaComponents/CommsPreferencesForm';
import Lists from '@pages/QuickFilterListCreatorPage/criteriaComponents/Lists';
import VolunteerPathwaysForm from '@pages/QuickFilterListCreatorPage/criteriaComponents/PathwaySteps';
import { FilterPanelPartCProps } from './types';

export default function FilterPanelPartC({
  filterCriteria,
  dispatchFilterCriteria,
  accordionStates,
  setAccordionStates,
  disabled,
  setIsCallingStatusDateFilterInvalid,
}: FilterPanelPartCProps) {
  usePrefetchPathwaySteps();

  return (
    <>
      <DrawerAccordion
        inUse={filterCriteria.tags.length > 0}
        headerText="Tags"
        expanded={accordionStates.tags}
        onChange={(_evt, ex) => setAccordionStates({ ...accordionStates, tags: ex })}
        disabled={disabled}
      >
        <TagsFetchAutocomplete
          value={filterCriteria.tags}
          onChange={(payload) => dispatchFilterCriteria({ type: 'setTags', payload })}
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={filterCriteria.groups.length > 0}
        headerText="Groups"
        expanded={accordionStates.groups}
        onChange={(_evt, ex) => setAccordionStates({ ...accordionStates, groups: ex })}
        disabled={disabled}
      >
        <GroupsFetchAutocomplete
          value={filterCriteria.groups}
          onChange={(payload) => dispatchFilterCriteria({ type: 'setGroups', payload })}
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={filterCriteria.lists.length > 0}
        headerText="Lists"
        expanded={accordionStates.lists}
        onChange={(_evt, ex) => setAccordionStates({ ...accordionStates, lists: ex })}
        disabled={disabled}
      >
        <Lists
          value={filterCriteria.lists}
          onChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setLists',
              payload: isTemplatePlaceholder(payload) ? [] : payload,
            })
          }
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={Boolean(filterCriteria.age?.value)}
        headerText="Age"
        expanded={accordionStates.age}
        onChange={(_evt, ex) => setAccordionStates({ ...accordionStates, age: ex })}
        disabled={disabled}
      >
        <Age
          onChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setAge',
              payload,
            })
          }
          defaultValue={filterCriteria.age}
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={filterCriteria.pathwaySteps.length > 0}
        headerText="Volunteer pathway"
        expanded={accordionStates.pathwaySteps}
        onChange={(_evt, open) => setAccordionStates({ ...accordionStates, pathwaySteps: open })}
        disabled={disabled}
      >
        <VolunteerPathwaysForm
          onChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setPathwaySteps',
              payload,
            })
          }
          value={filterCriteria.pathwaySteps}
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={filterCriteria.callingstatuses && filterCriteria.callingstatuses?.length > 0}
        headerText="Calling status"
        expanded={accordionStates.callingStatus}
        onChange={(_evt, open) => setAccordionStates({ ...accordionStates, callingStatus: open })}
        disabled={disabled}
      >
        <CallingStatusForm
          callingStatusValue={filterCriteria.callingstatuses ?? []}
          onCallingStatusChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setCallingStatuses',
              payload: isTemplatePlaceholder(payload) ? [] : payload,
            })
          }
          callingStatusInclude={filterCriteria.callingstatusinclusion ?? false}
          setCallingStatusInclude={(payload) =>
            dispatchFilterCriteria({ type: 'setCallingStatusInclusion', payload })
          }
          setIsFilterInvalid={setIsCallingStatusDateFilterInvalid}
          callingStatusDateValue={filterCriteria.callingstatusDateRange}
          onCallingStatusDateChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setCallingStatusDateRange',
              payload: {
                ...payload,
                callingPeriodQuantity: isTemplatePlaceholder(payload.callingPeriodQuantity)
                  ? 0
                  : payload.callingPeriodQuantity,
                displayedStartDate: isTemplatePlaceholder(payload.displayedStartDate)
                  ? null
                  : payload.displayedStartDate,
                displayedEndDate: isTemplatePlaceholder(payload.displayedEndDate)
                  ? null
                  : payload.displayedEndDate,
              },
            })
          }
          callingStatusLists={filterCriteria.callingstatusLists}
          onCallingStatusListsChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setCallingStatusLists',
              payload: isTemplatePlaceholder(payload) ? [] : payload,
            })
          }
          callingStatusUsers={filterCriteria.callingstatusUsers}
          onCallingStatusUsersChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setCallingStatusUsers',
              payload: isTemplatePlaceholder(payload) ? [] : payload,
            })
          }
          callingStatusCampaigns={filterCriteria.callingstatusCampaigns}
          onCallingStatusCampaignsChange={(payload) =>
            dispatchFilterCriteria({
              type: 'setCallingStatusCampaigns',
              payload: isTemplatePlaceholder(payload) ? [] : payload,
            })
          }
        />
      </DrawerAccordion>
      <DrawerAccordion
        inUse={filterCriteria.commspreferences && filterCriteria.commspreferences?.length > 0}
        headerText="Comms preferences"
        expanded={accordionStates.commsPreferences}
        onChange={(_evt, open) =>
          setAccordionStates({ ...accordionStates, commsPreferences: open })
        }
        disabled={disabled}
      >
        <CommsPreferencesForm
          commsPreferencesValue={filterCriteria.commspreferences ?? []}
          onCommsPreferencesChange={(_e, payload) =>
            dispatchFilterCriteria({ type: 'setCommsPreferences', payload })
          }
          commsPreferencesInclude={filterCriteria.commspreferencesinclusion ?? false}
          setCommsPreferencesInclude={(payload) =>
            dispatchFilterCriteria({ type: 'setCommsPreferencesInclusion', payload })
          }
        />
      </DrawerAccordion>
    </>
  );
}
